import React, { useEffect, useState } from "react";
import { HeartHandshake, Search, UserPlus, X, Trash2, ClipboardList, Plus, Video, MessageSquareText, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, isSuperAdmin } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function FamiliasAdmin() {
  const [familias, setFamilias] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [linkFor, setLinkFor] = useState(null);
  const [desarrolloFor, setDesarrolloFor] = useState(null);
  const [delParent, setDelParent] = useState(null);
  const [unlink, setUnlink] = useState(null);
  const [tab, setTab] = useState("vinculos");
  const [sendingWeekly, setSendingWeekly] = useState(false);

  const sendWeekly = async () => {
    if (sendingWeekly) return;
    setSendingWeekly(true);
    try {
      const r = await adminApi.familias.runWeeklySummary();
      toast.success(`Resumen semanal enviado a ${r.enviados} padre(s)/madre(s).`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingWeekly(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const d = await adminApi.familias.list(q); setFamilias(d.familias); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doUnlink = async () => {
    try { await adminApi.familias.desvincular(unlink.parentId, unlink.hijoId); toast.success("Vínculo eliminado."); setUnlink(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doDeleteParent = async () => {
    try { await adminApi.familias.deleteParent(delParent.id); toast.success("Cuenta de padre/madre eliminada."); setDelParent(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <HeartHandshake className="w-7 h-7 text-[var(--gold)]" />
        <h1 className="text-2xl font-extrabold text-[#0A1C30]">Familias Supera</h1>
        <button
          data-testid="send-weekly-summary-btn"
          onClick={sendWeekly}
          disabled={sendingWeekly}
          title="Envía ahora el resumen semanal de progreso a todos los padres/madres (automático cada domingo 19:00 CDMX)"
          className="ml-auto inline-flex items-center gap-2 rounded-xl bg-[#0A1C30] hover:bg-[#0E2740] text-white font-bold px-4 py-2 text-sm transition-colors disabled:opacity-60"
        >
          {sendingWeekly ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar resumen semanal
        </button>
      </div>
      <p className="text-sm text-[#6B7688] mb-6">Gestiona los vínculos entre padres/madres y sus candidatos/as, y el Desarrollo Personalizado de cada hijo/a. El resumen semanal de progreso se envía automáticamente cada domingo a las 19:00 (CDMX).</p>

      <div className="flex gap-2 mb-6 border-b border-black/5">
        {[["vinculos", "Vínculos familiares"], ["contenido", "Contenido de Padres"], ["evaluaciones", "Evaluaciones"], ["respuestas", "Respuestas de padres"]].map(([k, l]) => (
          <button key={k} data-testid={`fam-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === k ? "border-[var(--gold)] text-[#0A1C30]" : "border-transparent text-[#9AA3B2] hover:text-[#0A1C30]"}`}>{l}</button>
        ))}
      </div>

      {tab === "contenido" ? <ContenidoManager /> : tab === "evaluaciones" ? <EvalManager /> : tab === "respuestas" ? <RespuestasManager /> : (
      <>
      <div className="flex gap-2 mb-6 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#6B7688] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="familias-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar padre/madre por nombre o correo…" className="pl-9" />
        </div>
        <button data-testid="familias-search-btn" onClick={load} className="rounded-xl bg-[#0A1C30] text-white font-bold px-5 text-sm">Buscar</button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6B7688]"><Loader2 className="w-5 h-5 animate-spin" /> Cargando familias…</div>
      ) : familias.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-12 text-center">
          <HeartHandshake className="w-12 h-12 text-[#C7CDD6] mx-auto" />
          <p className="mt-3 text-[#6B7688] font-semibold">Aún no hay padres/madres registrados.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="familias-list">
          {familias.map((f) => (
            <div key={f.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5" data-testid={`familia-${f.email}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-extrabold text-[#0A1C30]">{f.nombre}</p>
                  <p className="text-sm text-[#6B7688]">{f.email}{f.whatsapp ? ` · ${f.whatsapp}` : ""}</p>
                </div>
                <button data-testid={`familia-delete-${f.email}`} onClick={() => setDelParent(f)} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C24141] hover:bg-[#FBEAEA] rounded-lg px-2.5 py-1.5 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Eliminar cuenta</button>
              </div>

              <div className="mt-4">
                <p className="text-xs font-bold text-[#9AA3B2] uppercase tracking-wide mb-2">Candidatos/as vinculados ({f.hijos.length})</p>
                {f.hijos.length === 0 ? (
                  <p className="text-sm text-[#C24141] font-semibold">Sin hijos/as vinculados.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {f.hijos.map((h) => (
                      <div key={h.id} className="rounded-xl bg-[#F4F6F9] p-3 flex items-center justify-between gap-2" data-testid={`hijo-chip-${h.email}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#0A1C30] truncate">{h.nombre}</p>
                          <p className="text-xs text-[#6B7688] truncate">{h.programa}{h.grupo ? ` · Grupo ${h.grupo}` : ""} · {h.email}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button data-testid={`desarrollo-${h.email}`} onClick={() => setDesarrolloFor(h)} title="Desarrollo Personalizado" className="w-8 h-8 rounded-lg hover:bg-white text-[#7C4DDB] flex items-center justify-center"><ClipboardList className="w-4 h-4" /></button>
                          <button data-testid={`unlink-${h.email}`} onClick={() => setUnlink({ parentId: f.id, hijoId: h.id, nombre: h.nombre })} title="Desvincular" className="w-8 h-8 rounded-lg hover:bg-white text-[#C24141] flex items-center justify-center"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <button data-testid={`link-${f.email}`} onClick={() => setLinkFor(f)} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#7C4DDB] text-white font-bold px-4 py-2 text-sm hover:bg-[#6a3fc0]"><UserPlus className="w-4 h-4" /> Vincular candidato/a</button>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {linkFor && <LinkDialog parent={linkFor} onClose={() => setLinkFor(null)} onLinked={() => { setLinkFor(null); load(); }} />}
      {desarrolloFor && <DesarrolloDialog hijo={desarrolloFor} onClose={() => setDesarrolloFor(null)} />}

      <AlertDialog open={!!unlink} onOpenChange={(o) => !o && setUnlink(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Desvincular a {unlink?.nombre}?</AlertDialogTitle>
            <AlertDialogDescription>El padre/madre dejará de tener acceso a la información de este candidato/a.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doUnlink} className="bg-[#C24141]">Desvincular</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delParent} onOpenChange={(o) => !o && setDelParent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar la cuenta de {delParent?.nombre}?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará la cuenta del padre/madre y sus vínculos. Los candidatos/as NO se eliminan. Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteParent} className="bg-[#C24141]">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LinkDialog({ parent, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    setBusy(true);
    try { const d = await adminApi.familias.searchCandidatos(q); setResults(d.candidatos); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);
  const link = async (c) => {
    try { await adminApi.familias.vincular(parent.id, c.id); toast.success(`${c.nombre} vinculado/a.`); onLinked(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="text-lg font-extrabold text-[#0A1C30]">Vincular candidato/a a {parent.nombre}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Buscar por nombre, correo o referencia…" data-testid="link-search" />
          <button onClick={search} className="rounded-xl bg-[#0A1C30] text-white font-bold px-4 text-sm">Buscar</button>
        </div>
        <div className="mt-3 max-h-80 overflow-y-auto space-y-2">
          {busy ? <p className="text-sm text-[#6B7688]">Buscando…</p> : results.length === 0 ? <p className="text-sm text-[#6B7688]">Sin resultados.</p> :
            results.map((c) => (
              <div key={c.id} className="rounded-xl ring-1 ring-black/5 p-3 flex items-center justify-between gap-2">
                <div className="min-w-0"><p className="text-sm font-bold text-[#0A1C30] truncate">{c.nombre}</p><p className="text-xs text-[#6B7688] truncate">{c.programa}{c.grupo ? ` · Grupo ${c.grupo}` : ""} · {c.email} · {c.referencia}</p></div>
                <button data-testid={`link-add-${c.email}`} onClick={() => link(c)} className="shrink-0 rounded-lg bg-[#7C4DDB] text-white font-bold px-3 py-1.5 text-xs hover:bg-[#6a3fc0]">Vincular</button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DesarrolloDialog({ hijo, onClose }) {
  const [data, setData] = useState(null);
  const [retro, setRetro] = useState("");
  const [dlg, setDlg] = useState({ titulo: "", grabacion_url: "", acuerdos: "", fecha: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const d = await adminApi.familias.getDesarrollo(hijo.id); setData(d.desarrollo); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addRetro = async () => {
    if (!retro.trim()) return;
    setSaving(true);
    try { await adminApi.familias.addRetro(hijo.id, retro); setRetro(""); await load(); toast.success("Retroalimentación agregada."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const delRetro = async (id) => { try { await adminApi.familias.delRetro(hijo.id, id); await load(); } catch (e) { toast.error(apiError(e)); } };
  const addDialogo = async () => {
    if (!dlg.grabacion_url.trim() && !dlg.acuerdos.trim()) { toast.error("Agrega al menos la liga de grabación o los acuerdos."); return; }
    setSaving(true);
    try { await adminApi.familias.addDialogo(hijo.id, dlg); setDlg({ titulo: "", grabacion_url: "", acuerdos: "", fecha: new Date().toISOString().slice(0, 10) }); await load(); toast.success("Diálogo mensual agregado."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const delDialogo = async (id) => { try { await adminApi.familias.delDialogo(hijo.id, id); await load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="desarrollo-dialog">
        <DialogHeader><DialogTitle className="text-lg font-extrabold text-[#0A1C30] flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#7C4DDB]" /> Desarrollo Personalizado · {hijo.nombre}</DialogTitle></DialogHeader>

        {/* Retroalimentación del staff */}
        <div className="mt-2">
          <Label className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-1.5"><MessageSquareText className="w-4 h-4" /> Retroalimentación del staff</Label>
          <div className="mt-2 flex gap-2">
            <Textarea data-testid="retro-input" value={retro} onChange={(e) => setRetro(e.target.value)} placeholder="Escribe retroalimentación sobre el desempeño del candidato/a…" className="min-h-[70px]" />
            <button data-testid="retro-add" onClick={addRetro} disabled={saving} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-4 self-stretch disabled:opacity-60">Agregar</button>
          </div>
          <div className="mt-3 space-y-2">
            {(data?.retroalimentaciones || []).map((r) => (
              <div key={r.id} className="rounded-xl bg-[#F4F6F9] p-3 flex items-start justify-between gap-2">
                <div><p className="text-sm text-[#2A3446] whitespace-pre-wrap">{r.texto}</p><p className="text-[11px] text-[#9AA3B2] mt-1">{r.autor} · {(r.fecha || "").slice(0, 10)}</p></div>
                <button onClick={() => delRetro(r.id)} className="text-[#C24141] shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(!data?.retroalimentaciones || data.retroalimentaciones.length === 0) && <p className="text-xs text-[#9AA3B2]">Aún no hay retroalimentación.</p>}
          </div>
        </div>

        {/* Grabaciones Diálogo Mensual */}
        <div className="mt-6 pt-5 border-t border-black/5">
          <Label className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-1.5"><Video className="w-4 h-4" /> Grabaciones Diálogo Mensual + acuerdos</Label>
          <div className="mt-2 rounded-xl bg-[#F6F3FC] p-4 space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <Input data-testid="dialogo-titulo" value={dlg.titulo} onChange={(e) => setDlg((p) => ({ ...p, titulo: e.target.value }))} placeholder="Título (ej. Diálogo Agosto)" />
              <Input data-testid="dialogo-fecha" type="date" value={dlg.fecha} onChange={(e) => setDlg((p) => ({ ...p, fecha: e.target.value }))} />
            </div>
            <Input data-testid="dialogo-url" value={dlg.grabacion_url} onChange={(e) => setDlg((p) => ({ ...p, grabacion_url: e.target.value }))} placeholder="Liga de la grabación (Zoom, Drive, YouTube…)" />
            <Textarea data-testid="dialogo-acuerdos" value={dlg.acuerdos} onChange={(e) => setDlg((p) => ({ ...p, acuerdos: e.target.value }))} placeholder="Acuerdos y compromisos del diálogo mensual…" className="min-h-[70px]" />
            <button data-testid="dialogo-add" onClick={addDialogo} disabled={saving} className="inline-flex items-center gap-1.5 rounded-xl bg-[#7C4DDB] text-white font-bold px-4 py-2 text-sm disabled:opacity-60"><Plus className="w-4 h-4" /> Agregar diálogo</button>
          </div>
          <div className="mt-3 space-y-2">
            {(data?.dialogos || []).map((d) => (
              <div key={d.id} className="rounded-xl ring-1 ring-black/5 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-bold text-[#0A1C30]">{d.titulo} <span className="text-xs font-normal text-[#6B7688]">· {d.fecha}</span></p>
                  <button onClick={() => delDialogo(d.id)} className="text-[#C24141] shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
                {d.grabacion_url && <a href={d.grabacion_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#7C4DDB] hover:underline break-all">{d.grabacion_url}</a>}
                {d.acuerdos && <p className="text-sm text-[#2A3446] mt-1 whitespace-pre-wrap">{d.acuerdos}</p>}
              </div>
            ))}
            {(!data?.dialogos || data.dialogos.length === 0) && <p className="text-xs text-[#9AA3B2]">Aún no hay diálogos registrados.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SECCIONES = [
  ["sesiones_prox", "Próximas sesiones"], ["sesiones_grab", "Sesiones grabadas"],
  ["podcast", "Podcast"], ["libro", "Libro del mes"], ["market", "Supera Market"], ["investors", "Supera Investors"],
];

function ContenidoManager() {
  const [seccion, setSeccion] = useState("sesiones_prox");
  const [items, setItems] = useState([]);
  const empty = { titulo: "", descripcion: "", url: "", imagen_url: "", fecha: "", hora: "", orden: 1, activo: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const load = async () => { try { const d = await adminApi.familias.listContenido(seccion); setItems(d.items); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [seccion]);
  const add = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio."); return; }
    setSaving(true);
    try { await adminApi.familias.createContenido({ ...form, seccion }); setForm(empty); await load(); toast.success("Contenido agregado."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (it) => { try { await adminApi.familias.updateContenido(it.id, { activo: !it.activo }); await load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await adminApi.familias.delContenido(id); await load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {SECCIONES.map(([k, l]) => (
          <button key={k} data-testid={`cont-sec-${k}`} onClick={() => setSeccion(k)} className={`rounded-xl px-3.5 py-2 text-sm font-bold ${seccion === k ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>{l}</button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
          <p className="font-extrabold text-[#0A1C30] mb-3">Agregar contenido</p>
          <div className="space-y-2.5">
            <Input data-testid="cont-titulo" value={form.titulo} onChange={(e) => set("titulo", e.target.value)} placeholder="Título" />
            <Textarea value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} placeholder="Descripción" className="min-h-[70px]" />
            <Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="Liga (Zoom, YouTube, Drive, Spotify…)" />
            <Input value={form.imagen_url} onChange={(e) => set("imagen_url", e.target.value)} placeholder="URL de imagen (opcional)" />
            <div className="grid grid-cols-3 gap-2">
              <Input type="date" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} />
              <Input type="time" value={form.hora} onChange={(e) => set("hora", e.target.value)} />
              <Input type="number" value={form.orden} onChange={(e) => set("orden", e.target.value)} placeholder="Orden" />
            </div>
            <button data-testid="cont-add" onClick={add} disabled={saving} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 py-2.5 disabled:opacity-60 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Agregar</button>
          </div>
        </div>
        <div className="space-y-2">
          {items.length === 0 ? <p className="text-sm text-[#9AA3B2]">Sin contenido en esta sección.</p> :
            items.map((it) => (
              <div key={it.id} className={`bg-white rounded-xl ring-1 ring-black/5 p-3 flex items-center justify-between gap-2 ${it.activo ? "" : "opacity-60"}`} data-testid={`cont-item-${it.id}`}>
                <div className="min-w-0"><p className="text-sm font-bold text-[#0A1C30] truncate">{it.titulo}</p><p className="text-xs text-[#6B7688] truncate">{[it.fecha, it.hora, it.url].filter(Boolean).join(" · ")}</p></div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggle(it)} className="text-xs font-bold px-2 py-1 rounded-lg bg-[#F4F6F9]">{it.activo ? "Activo" : "Oculto"}</button>
                  <button onClick={() => del(it.id)} className="text-[#C24141]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function EvalManager() {
  const [tipo, setTipo] = useState("leadership");
  const [preguntas, setPreguntas] = useState([]);
  const [texto, setTexto] = useState("");
  const load = async () => { try { const d = await adminApi.familias.listEval(tipo); setPreguntas(d.preguntas); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tipo]);
  const add = async () => {
    if (!texto.trim()) { toast.error("Escribe la pregunta."); return; }
    try { await adminApi.familias.createEval({ tipo, texto, orden: preguntas.length + 1 }); setTexto(""); await load(); toast.success("Pregunta agregada."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await adminApi.familias.delEval(id); await load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <div className="flex gap-2 mb-5">
        <button data-testid="evalm-tab-leadership" onClick={() => setTipo("leadership")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tipo === "leadership" ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>Evaluar Supera Leadership</button>
        <button data-testid="evalm-tab-instructor" onClick={() => setTipo("instructor")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tipo === "instructor" ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>Evaluar Instructor</button>
      </div>
      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5 max-w-2xl">
        <div className="flex gap-2 mb-4">
          <Input data-testid="evalm-input" value={texto} onChange={(e) => setTexto(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Escribe una nueva pregunta…" />
          <button data-testid="evalm-add" onClick={add} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Agregar</button>
        </div>
        {preguntas.length === 0 ? <p className="text-sm text-[#9AA3B2]">Sin preguntas para esta evaluación.</p> :
          <div className="space-y-2">{preguntas.map((p, i) => (
            <div key={p.id} className="flex items-center justify-between gap-2 rounded-xl bg-[#F4F6F9] p-3">
              <p className="text-sm text-[#2A3446]">{i + 1}. {p.texto}</p>
              <button onClick={() => del(p.id)} className="text-[#C24141] shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}</div>}
      </div>
    </div>
  );
}


const TIPO_LABEL = { leadership: "Supera Leadership", instructor: "Instructor" };

function StarRating({ value }) {
  const v = Number(value) || 0;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`text-base leading-none ${n <= v ? "text-[var(--gold)]" : "text-[#D5DAE1]"}`}>★</span>
      ))}
      <span className="ml-1 text-xs font-bold text-[#6B7688]">{typeof value === "number" ? value : ""}</span>
    </span>
  );
}

function RespuestasManager() {
  const [tipo, setTipo] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const superAdmin = isSuperAdmin();

  const load = async () => {
    setLoading(true);
    try { const d = await adminApi.familias.listEvalRespuestas(tipo); setItems(d.respuestas || []); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tipo]);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta respuesta de evaluación?")) return;
    try { await adminApi.familias.delEvalRespuesta(id); toast.success("Respuesta eliminada."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const fmtFecha = (s) => { try { return new Date(s).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" }); } catch { return s || "—"; } };
  const TABS = [["", "Todas"], ["leadership", "Supera Leadership"], ["instructor", "Instructor"]];

  return (
    <div data-testid="fam-respuestas">
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([k, l]) => (
          <button key={k || "all"} data-testid={`resp-tab-${k || "all"}`} onClick={() => setTipo(k)} className={`rounded-xl px-4 py-2 text-sm font-bold ${tipo === k ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6B7688]"><Loader2 className="w-5 h-5 animate-spin" /> Cargando respuestas…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-12 text-center">
          <ClipboardList className="w-12 h-12 text-[#C7CDD6] mx-auto" />
          <p className="mt-3 text-[#6B7688] font-semibold">Aún no hay respuestas de padres para esta evaluación.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden" data-testid={`resp-row-${r.id}`}>
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="font-extrabold text-[#0A1C30] truncate">{r.user_nombre || r.user_email || "Padre/Madre"}</p>
                  <p className="text-xs text-[#6B7688] truncate">
                    <span className="inline-flex items-center rounded-full bg-[#0A1C30] text-white font-bold px-2 py-0.5 mr-2">{TIPO_LABEL[r.tipo] || r.tipo}</span>
                    {r.hijos && r.hijos.length > 0 ? `Hijo/a: ${r.hijos.join(", ")} · ` : ""}{fmtFecha(r.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {r.promedio != null && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#FFF7E6] text-[#8A6D1B] font-extrabold px-3 py-1 text-sm" title="Promedio de la evaluación">
                      ★ {r.promedio}
                    </span>
                  )}
                  <button data-testid={`resp-toggle-${r.id}`} onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="rounded-lg bg-[#F4F6F9] text-[#0A1C30] font-bold px-3 py-1.5 text-xs">
                    {expanded === r.id ? "Ocultar" : "Ver respuestas"}
                  </button>
                  {superAdmin && (
                    <button data-testid={`resp-del-${r.id}`} onClick={() => del(r.id)} title="Eliminar respuesta" className="inline-flex items-center justify-center rounded-lg bg-[#FCE4E4] text-[#C0392B] p-2 hover:bg-[#f7d3d3] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {expanded === r.id && (
                <div className="border-t border-black/5 bg-[#FAFAFA] p-4 space-y-2">
                  {(r.respuestas || []).map((a, i) => (
                    <div key={i} className="flex items-start justify-between gap-4 rounded-xl bg-white ring-1 ring-black/5 p-3">
                      <p className="text-sm text-[#2A3446] flex-1">{a.pregunta}</p>
                      {typeof a.valor === "number" ? <StarRating value={a.valor} /> : <span className="text-sm font-bold text-[#0A1C30]">{String(a.valor)}</span>}
                    </div>
                  ))}
                  {r.comentario && (
                    <div className="rounded-xl bg-[#FFFBEB] ring-1 ring-[var(--gold)]/30 p-3">
                      <p className="text-xs font-extrabold text-[#8A6D1B] uppercase tracking-wide mb-1">Comentario</p>
                      <p className="text-sm text-[#2A3446] italic">“{r.comentario}”</p>
                    </div>
                  )}
                  {(r.respuestas || []).length === 0 && !r.comentario && (
                    <p className="text-sm text-[#9AA3B2]">Sin respuestas registradas.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
